import React, { useEffect, useRef, useState } from "react";

import { ExportOutlined } from "@ant-design/icons";
import { GetRowIdParams, GridOptions } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Button, DatePicker, Flex, Typography } from "antd";
import { Dayjs } from "dayjs";
import i18next from "i18next";

import {
  useDownloadMpOrdersHistoryPerDepot,
  useDownloadMpOrdersHistoryPerDepotInExcel,
  useGetAllRealOrders,
} from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreServicesDtoMarketplaceOrderProductDto,
} from "api/reactQuery/querySchemas";
import Loader from "components/Loader";
import useColDefs from "components/MpOrder/OrderHistory/useColDefs";
import OrderTotals from "components/MpOrder/OrderTotals";
import TextArea from "components/TextArea";
import { downloadBlob } from "components/Utils/actions";
import { dateFormat, dateFormatForBE } from "components/Utils/dayjsUtils";
import { getGridDefaultProps } from "constants/agGridConstants";

const OrderHistory: React.FC = () => {
  const [startDate, setStartDate] = useState<Dayjs>();
  const [endDate, setEndDate] = useState<Dayjs>();
  const [selectedOrder, setSelectedOrder] = useState<BSCoreServicesDtoMarketplaceOrderDto>();
  const gridRef = useRef<AgGridReact<BSCoreServicesDtoMarketplaceOrderDto>>(null);
  const { data: orders, isLoading: isOrdersLoading } = useGetAllRealOrders(
    {
      queryParams: {
        endDate: endDate ? endDate?.format(dateFormatForBE) : "",
        startDate: startDate ? startDate?.format(dateFormatForBE) : "",
      },
    },
    { enabled: !!startDate || !!endDate }
  );
  const {
    mutate: downloadOrdersHistoryInExcel,
    isLoading: downloadOrdersHistoryInExcelLoading,
  } = useDownloadMpOrdersHistoryPerDepotInExcel({
    onSuccess: (data) =>
      downloadBlob(
        (data as unknown as { blob: Blob }).blob,
        `Order_${selectedOrder.id}.xlsx`,
      ),
  });
  const {
    mutate: downloadOrdersHistoryInPDF,
    isLoading: downloadOrdersHistoryInPDFLoading,
  } = useDownloadMpOrdersHistoryPerDepot({
    onSuccess: (data) =>
      downloadBlob(
        (data as unknown as { blob: Blob }).blob,
        `Order_${selectedOrder.id}.pdf`,
      ),
  });

  const { orderColumnDefs, productColumnDefs } = useColDefs();

  const orderData = {
    bonusOnInvoiceProducts: selectedOrder?.products?.filter(
      (product) => product.isBonusInvoiceProduct
    ),
    ncpCompensationProducts: selectedOrder?.products?.filter(
      (product) => product.isNcpCompensationProduct
    ),
    gasProducts: selectedOrder?.products?.filter(
      (product) => product.productTypeId === 1 || product.productTypeId === 2
    ),
    multiPackProducts: selectedOrder?.products?.filter(
      (product) => product.isMultiPack
    ),
    onInvoiceProducts: selectedOrder?.products?.filter(
      (product) => product.isInvoiceProduct
    ),
    products: selectedOrder?.products?.filter(
      (product) =>
        !product.isInvoiceProduct &&
        !product.isMultiPack &&
        !product.isBonusInvoiceProduct &&
        !product.isNcpCompensationProduct &&
        product.productTypeId !== 1 &&
        product.productTypeId !== 2
    ),
    total: selectedOrder?.total,
  };
  const onRowSelected: GridOptions<BSCoreServicesDtoMarketplaceOrderDto>["onRowSelected"] =
    (event) => event?.event && setSelectedOrder(event.data);

  const onDateChangeHandler = async (dates: Dayjs[]) => {
    if (dates) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    }
    setSelectedOrder(undefined);
  };

  const updatePinnedBottomRowData = () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    const allRowData: BSCoreServicesDtoMarketplaceOrderDto[] = [];
    gridApi.forEachNodeAfterFilterAndSort((node) => allRowData.push(node.data));

    const calculateTotal = (field: string) =>
      allRowData?.reduce(
        (sum, row) => Number(sum) + Number(row.total?.[field] || 0),
        0
      );

    const totalTtlVolumeInHL = calculateTotal("ttlVolumeInHL");
    const totalTtlPackagingCost = calculateTotal("ttlPackagingCost");
    const totalTtlBeerCostWithoutVAT = calculateTotal("ttlBeerCostWithoutVAT");
    const totalTtlBeerCostWithVAT = calculateTotal("ttlBeerCostWithVAT");
    const totalTtlBeerWithVATAndPackagingCost = calculateTotal("ttlBeerWithVATAndPackagingCost");

    const pinnedBottomRowData = [
      {
        total: {
          ttlBeerCostWithVAT: totalTtlBeerCostWithVAT,
          ttlBeerCostWithoutVAT: totalTtlBeerCostWithoutVAT,
          ttlBeerWithVATAndPackagingCost: totalTtlBeerWithVATAndPackagingCost,
          ttlPackagingCost: totalTtlPackagingCost,
          ttlVolumeInHL: totalTtlVolumeInHL,
        },
      },
    ];

    gridApi.setGridOption("pinnedBottomRowData", pinnedBottomRowData);
  };

  useEffect(() => {
    const currentGridRefApi = gridRef.current?.api;
    if (currentGridRefApi) {
      currentGridRefApi.addEventListener(
        "filterChanged",
        updatePinnedBottomRowData
      );
      updatePinnedBottomRowData();
    }

    return () => {
      if (currentGridRefApi) {
        currentGridRefApi.removeEventListener(
          "filterChanged",
          updatePinnedBottomRowData
        );
      }
    };
  }, [orders]);

  return (
    <>
      <Typography.Title level={3} style={{ marginBottom: 20 }}>
        {i18next.t("history_of_orders")}
      </Typography.Title>
      <Flex align="center" justify="space-between">
        <div>
          <Typography.Text style={{ marginRight: 10 }}>
            {i18next.t("select_period")}
          </Typography.Text>
          <DatePicker.RangePicker
            format={dateFormat}
            defaultValue={[startDate, endDate]}
            onChange={onDateChangeHandler}
          />
        </div>
        {orders?.length > 0 && (
          <Flex>
            <Button
              style={{ marginRight: "10px", padding: "0 50px" }}
              type="primary"
              loading={downloadOrdersHistoryInExcelLoading}
              onClick={() => downloadOrdersHistoryInExcel({ body: selectedOrder })}
              disabled={!selectedOrder}
            >
              <ExportOutlined style={{ verticalAlign: "middle" }} />
              {i18next.t("export_in_excel")}
            </Button>
            <Button
              style={{ padding: "0 50px" }}
              type="primary"
              loading={downloadOrdersHistoryInPDFLoading}
              onClick={() => downloadOrdersHistoryInPDF({ body: selectedOrder })}
              disabled={!selectedOrder}
            >
              <ExportOutlined style={{ verticalAlign: "middle" }} />
              {i18next.t("export_in_pdf")}
            </Button>
          </Flex>
        )}
      </Flex>

      {isOrdersLoading && startDate && endDate && (
        <Loader background="semiLight" isLoading={isOrdersLoading} />
      )}

      {orders?.length > 0 && (
        <div style={{ height: 308 }}>
          <AgGridReact
            getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderDto>) => String(params.data.id)}
            rowData={orders}
            ref={gridRef}
            onGridReady={updatePinnedBottomRowData}
            columnDefs={orderColumnDefs}
            rowSelection="single"
            onRowSelected={onRowSelected}
            {...getGridDefaultProps()}
          />
        </div>
      )}

      {selectedOrder ? (
        <Flex style={{ margin: "20px 0 30px", width: "100%" }}>
          {!!orderData?.total && (
            <OrderTotals totals={orderData.total || orderData.total} />
          )}
        </Flex>
      ) : (
        !selectedOrder &&
        orders?.length > 0 && (
          <Typography.Title
            level={1}
            style={{
              color: "#6D6D6D",
              marginTop: 150,
              textAlign: "center",
            }}
          >
            {i18next.t("please_select_order_row_from_the_table")}
          </Typography.Title>
        )
      )}

      {selectedOrder?.comment && (
        <Typography.Title level={3}>
          {i18next.t("order_comment")}
        </Typography.Title>
      )}

      {selectedOrder?.comment && (
        <div style={{ margin: "0 0 25px 0" }}>
          <TextArea
            value={selectedOrder.comment}
            readOnly
            style={{ pointerEvents: "none", resize: "none" }}
          />
        </div>
      )}

      {!!orderData?.bonusOnInvoiceProducts?.length && (
        <>
          <Typography.Title level={3}>
            {i18next.t("bonus_on_invoice_products")}
          </Typography.Title>
          <div style={{ height: 220, margin: "10px 0" }}>
            <AgGridReact
              getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderProductDto>) => String(params.data.id)}
              rowData={orderData.bonusOnInvoiceProducts}
              columnDefs={productColumnDefs}
              {...getGridDefaultProps()}
            />
          </div>
        </>
      )}

      {!!orderData?.ncpCompensationProducts?.length && (
        <>
          <Typography.Title level={3}>
            {i18next.t("ncp_compensation_products")}
          </Typography.Title>
          <div style={{ height: 220, margin: "10px 0" }}>
            <AgGridReact
              getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderProductDto>) => String(params.data.id)}
              rowData={orderData.ncpCompensationProducts}
              columnDefs={productColumnDefs}
              {...getGridDefaultProps()}
            />
          </div>
        </>
      )}

      {!!orderData?.onInvoiceProducts?.length && (
        <>
          <Typography.Title level={3}>
            {i18next.t("on_invoice_products")}
          </Typography.Title>
          <div style={{ height: 220, margin: "10px 0" }}>
            <AgGridReact
              getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderProductDto>) => String(params.data.id)}
              rowData={orderData.onInvoiceProducts}
              columnDefs={productColumnDefs}
              {...getGridDefaultProps()}
            />
          </div>
        </>
      )}

      {!!orderData?.multiPackProducts?.length && (
        <>
          <Typography.Title level={3}>
            {i18next.t("multipack_products")}
          </Typography.Title>
          <div style={{ height: 220, margin: "10px 0" }}>
            <AgGridReact
              getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderProductDto>) => String(params.data.id)}
              rowData={orderData.multiPackProducts}
              columnDefs={productColumnDefs}
              {...getGridDefaultProps()}
            />
          </div>
        </>
      )}

      {!!orderData?.products?.length && (
        <>
          <Typography.Title level={3}>{i18next.t("products")}</Typography.Title>
          <div style={{ height: 400, margin: "10px 0" }}>
            <AgGridReact
              getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderProductDto>) => String(params.data.id)}
              rowData={orderData.products}
              columnDefs={productColumnDefs}
              {...getGridDefaultProps()}
            />
          </div>
        </>
      )}

      {!!orderData?.gasProducts?.length && (
        <>
          <Typography.Title level={3}>
            {i18next.t("gas_products")}
          </Typography.Title>
          <div style={{ height: 400, margin: "10px 0" }}>
            <AgGridReact
              getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceOrderProductDto>) => String(params.data.id)}
              rowData={orderData.gasProducts}
              columnDefs={productColumnDefs}
              {...getGridDefaultProps()}
            />
          </div>
        </>
      )}
    </>
  );
};
export default OrderHistory;
